import { useCallback, useMemo, useState } from "react";
import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from "@tanstack/react-table";
import { useNavigate } from "@tanstack/react-router";
import { Layers, Plus, Trash2 } from "lucide-react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import {
  MasterBanner,
  MasterFilterBar,
  MasterHeader,
  MasterPage,
  MasterSearchInput,
  MasterTabs,
  useMasterListShortcuts,
} from "@/components/shared/master-shell";
import { Badge, StatusDot } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { NativeSelect } from "@/components/ui/input";
import { useDeleteMaterial, useMaterials } from "@/features/materials/api";
import { MATERIAL_CATEGORIES, labelFor } from "@/features/materials/constants";
import { GradesPanel } from "@/features/materials/grades-panel";
import { ShapeSizesPanel } from "@/features/materials/shape-sizes-panel";
import { UomsPanel } from "@/features/materials/uoms-panel";
import type { MaterialListRow } from "@/features/materials/types";
import { isSupabaseConfigured } from "@/lib/supabase";

type HubTab = "materials" | "grades" | "shapes" | "uoms";

const columnHelper = createColumnHelper<MaterialListRow>();

export function MaterialsPage() {
  return (
    <ErrorBoundary title="Materials page crashed">
      <MaterialsPageInner />
    </ErrorBoundary>
  );
}

function MaterialsPageInner() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<HubTab>("materials");
  const { data = [], isLoading, error, refetch, isFetching } = useMaterials();
  const deleteMaterial = useDeleteMaterial();

  const [sorting, setSorting] = useState<SortingState>([{ id: "name", desc: false }]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("active");
  const [deleting, setDeleting] = useState<MaterialListRow | null>(null);

  const createMaterial = useCallback(() => {
    void navigate({ to: "/materials/new" });
  }, [navigate]);

  useMasterListShortcuts({
    searchInputId: "materials-search",
    onCreate: tab === "materials" ? createMaterial : undefined,
    enabled: tab === "materials",
  });

  const filtered = useMemo(
    () =>
      data.filter((m) => {
        if (statusFilter === "active" && !m.is_active) return false;
        if (statusFilter === "inactive" && m.is_active) return false;
        if (categoryFilter !== "all" && m.category !== categoryFilter) return false;
        return true;
      }),
    [data, statusFilter, categoryFilter],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        header: "Material",
        cell: ({ row }) => (
          <div className="min-w-0">
            <div className="truncate font-medium leading-tight">{row.original.name}</div>
            <div className="truncate font-mono text-2xs text-muted-foreground">
              {row.original.batch_prefix}
              {row.original.parent?.name ? ` · from ${row.original.parent.name}` : ""}
            </div>
          </div>
        ),
      }),
      columnHelper.accessor("category", {
        header: "Category",
        cell: (info) => (
          <Badge variant="outline">{labelFor(MATERIAL_CATEGORIES, info.getValue())}</Badge>
        ),
      }),
      columnHelper.display({
        id: "uom",
        header: "UOM",
        cell: ({ row }) => (
          <span className="font-mono text-xs">{row.original.uom?.code ?? "—"}</span>
        ),
      }),
      columnHelper.accessor("requires_shape_size", {
        header: "Shape",
        cell: (info) =>
          info.getValue() ? (
            <Badge variant="secondary">Required</Badge>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      }),
      columnHelper.accessor("is_active", {
        header: "Status",
        cell: (info) => (
          <StatusDot tone={info.getValue() ? "success" : "neutral"}>
            {info.getValue() ? "Active" : "Inactive"}
          </StatusDot>
        ),
      }),
      columnHelper.display({
        id: "actions",
        size: 40,
        cell: ({ row }) => (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={`Delete ${row.original.name}`}
            className="opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 hover:text-destructive"
            onClick={(e) => {
              e.stopPropagation();
              setDeleting(row.original);
            }}
          >
            <Trash2 />
          </Button>
        ),
      }),
    ],
    [],
  );

  const table = useReactTable({
    data: filtered,
    columns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getRowId: (row) => row.id,
  });

  const noMaterials = !isLoading && !error && data.length === 0;

  return (
    <MasterPage>
      <MasterHeader
        title="Materials"
        icon={<Layers />}
        countLabel={`${data.length} in catalogue`}
        onRefresh={() => void refetch()}
        refreshing={isFetching}
        actions={
          tab === "materials" ? (
            <Button type="button" size="sm" onClick={createMaterial}>
              <Plus />
              New material
            </Button>
          ) : null
        }
      />

      {!isSupabaseConfigured ? (
        <MasterBanner>Connect Supabase environment variables to enable live data.</MasterBanner>
      ) : null}

      <MasterTabs
        value={tab}
        onChange={setTab}
        options={[
          { value: "materials", label: "Materials" },
          { value: "grades", label: "Grades" },
          { value: "shapes", label: "Shapes" },
          { value: "uoms", label: "UOMs" },
        ]}
      />

      {tab === "materials" ? (
        <>
          {error ? <MasterBanner tone="danger">{(error as Error).message}</MasterBanner> : null}

          {noMaterials ? (
            <EmptyState
              title="No materials yet"
              description="Seed should include Coil, Slit, Sheet, Tube and CTL — or create your first material."
              action={
                <Button type="button" size="sm" onClick={createMaterial}>
                  <Plus />
                  Create material
                </Button>
              }
            />
          ) : (
            <>
              <MasterFilterBar shownLabel={`${table.getFilteredRowModel().rows.length} shown`}>
                <MasterSearchInput
                  id="materials-search"
                  value={globalFilter}
                  onChange={setGlobalFilter}
                  placeholder="Search materials…  (/)"
                />
                <NativeSelect
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
                  className="w-28"
                  aria-label="Status filter"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="all">All</option>
                </NativeSelect>
                <NativeSelect
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="w-36"
                  aria-label="Category filter"
                >
                  <option value="all">All categories</option>
                  {MATERIAL_CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </NativeSelect>
              </MasterFilterBar>

              <DataTable
                table={table}
                columns={columns}
                isLoading={isLoading}
                emptyTitle="No matching materials"
                onRowClick={(row) =>
                  void navigate({
                    to: "/materials/$materialId/edit",
                    params: { materialId: row.original.id },
                  })
                }
              />
            </>
          )}
        </>
      ) : null}

      {tab === "grades" ? <GradesPanel /> : null}
      {tab === "shapes" ? <ShapeSizesPanel /> : null}
      {tab === "uoms" ? <UomsPanel /> : null}

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(o) => {
          if (!o) setDeleting(null);
        }}
        title="Delete material?"
        description={
          deleting
            ? `${deleting.name} and its shape sizes will be removed. Prefer deactivate if it was used on documents.`
            : undefined
        }
        confirmLabel="Delete"
        destructive
        loading={deleteMaterial.isPending}
        onConfirm={async () => {
          if (!deleting) return;
          await deleteMaterial.mutateAsync(deleting.id);
          setDeleting(null);
        }}
      />
    </MasterPage>
  );
}
